/**
 * Vehicle DNA hook — backs the benchmark panel on the VehicleDetail page.
 *
 * Hits `GET /api/v1/vehicles/:registration/dna`, which folds four
 * consumption benchmarks (OEM spec, peer cohort, own baseline, own recent
 * actual) plus zone pricing and operating cost into one payload.
 *
 * Keyed on REGISTRATION, not asset token — the rest of the VehicleDetail
 * page is token-addressed, so the caller has to resolve the registration
 * from the profile query first and the hook stays disabled until it does.
 *
 * 4xx responses are NOT retried. A vehicle with no DNA row 404s, and the
 * panel's contract is to render nothing in that case — burning three
 * retries first would just delay the page settling.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { api } from '@/api/client';
import type { VehicleDnaResponse } from '@/types/vehicleDna';

const STALE_MS = 60_000;
const MAX_RETRIES = 2;

export function useVehicleDna(
  registration: string | undefined,
): UseQueryResult<VehicleDnaResponse, Error> {
  return useQuery<VehicleDnaResponse, Error>({
    queryKey: ['vehicle-dna', registration],
    enabled: Boolean(registration),
    queryFn: async () => {
      const { data } = await api.get<VehicleDnaResponse>(
        `/vehicles/${encodeURIComponent(registration as string)}/dna`,
      );
      return data;
    },
    staleTime: STALE_MS,
    retry: (failureCount, error) => {
      const status = (error as AxiosError)?.response?.status;
      if (status != null && status >= 400 && status < 500) return false;
      return failureCount < MAX_RETRIES;
    },
  });
}
